// Shared drawing kit for the procurement PDFs (Purchase Order, GRN).
//
// Both documents render the same "v5" system: a centred letterhead, bordered content
// cards, an outlined status chip, hairline-separated item rows and a light footer.
// Everything in here existed as a verbatim copy in both poPdf.js and grnPdf.js before;
// this module is the single copy, so the two documents cannot drift apart.
//
// Nothing document-specific lives here — column definitions, status vocabularies and
// the totals pipelines stay in their own builders.
import NavLogoHlthTek from '../../../assets/images/logo/NavLogo_new.png';
import { IPD_COLORS } from '../../ipd/utils/ipdColors';

export function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// ─── Palette ─────────────────────────────────────────────────────────────────
export const C = {
  navy:   hexToRgb(IPD_COLORS.primary),        // #1A2B4B  titles, card titles, values
  muted:  hexToRgb(IPD_COLORS.textSecondary),  // #64748B  labels, subtitle, terms
  faint:  hexToRgb(IPD_COLORS.textMuted),      // #94A3B8  registration line, HSN sub-line
  border: hexToRgb(IPD_COLORS.border),         // #E2E8F0  card borders, rules, row hairlines
  white:  hexToRgb(IPD_COLORS.white),
  // One step darker than IPD_COLORS.pageBg (#F8FAFC), which is so close to white it
  // disappears in print — the table head has to read as a band on paper.
  headBg: hexToRgb('#F1F5F9'),
};

// ─── Layout ──────────────────────────────────────────────────────────────────
export const SIDE_M = 10;           // page side margin
export const U = 190;               // usable width = 210 - 2*SIDE_M
export const CARD_GUTTER = 4;
export const CARD_W = (U - CARD_GUTTER) / 2;   // 93 — the 50/50 row
export const CARD_X_RIGHT = SIDE_M + CARD_W + CARD_GUTTER;  // 107

export const CARD_PAD_X = 3.2;
export const CARD_PAD_BOT = 2.6;
export const CARD_BORDER = 0.2;
export const CARD_R = 1.5;
export const CARD_HEAD_H = 8.6;     // card top → first body row
export const CARD_GAP = 3;          // between stacked cards

export const ROW_H = 4.3;           // label/value row pitch
export const WRAP_H = 3.4;          // extra advance per wrapped value line
export const LABEL_W = 25;
export const LINE_H = 3.9;          // free-text line pitch at 7pt

export const FOOTER_RESERVE = 20;
export const CONT_TOP = 24;         // content top on pages 2+
export const MAX_VALUE_LINES = 2;   // long addresses wrap, they do not truncate

export const PAGE_H = 297;
export const CONTENT_BOTTOM = PAGE_H - FOOTER_RESERVE;   // 277

const FOOTER_POWERED =
  'Powered by Hlthtek Optimus Private Limited  ·  www.hlthtek.com';

// Em dash for "no value". It has to be written as the WinAnsi byte 0x97, not as
// U+2014: jsPDF maps a standard font to /WinAnsiEncoding but does not translate
// codepoints above 0xFF into it, so a U+2014 literal is silently dropped from the
// output and the placeholder renders as blank. Verified against jspdf 2.5.1's
// dist/jspdf.es.min.js, which is the build webpack resolves.
export const DASH = '\u0097';

// ─── Value formatting ────────────────────────────────────────────────────────
// jsPDF's built-in Helvetica is WinAnsi and cannot render '₹', which is why this
// exists instead of formatCurrency/formatINR from ../utils.
export function Rs(value) {
  const n = Number(value);
  if (isNaN(n)) return DASH;
  return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function pct(value) {
  const n = Number(value);
  return isNaN(n) || n === 0 ? DASH : `${n}%`;
}

export function numberToWords(num) {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
    'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function inW(n) {
    if (n < 20)          return a[n];
    if (n < 100)         return b[Math.floor(n / 10)] + (n % 10 ? ` ${a[n % 10]}` : '');
    if (n < 1_000)       return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ` ${inW(n % 100)}` : '');
    if (n < 1_00_000)    return inW(Math.floor(n / 1_000)) + ' Thousand' + (n % 1_000 ? ` ${inW(n % 1_000)}` : '');
    if (n < 1_00_00_000) return inW(Math.floor(n / 1_00_000)) + ' Lakh' + (n % 1_00_000 ? ` ${inW(n % 1_00_000)}` : '');
    return inW(Math.floor(n / 1_00_00_000)) + ' Crore' + (n % 1_00_00_000 ? ` ${inW(n % 1_00_00_000)}` : '');
  }
  const n = Math.floor(Math.abs(Number(num) || 0));
  return (inW(n) || 'Zero') + ' Rupees Only';
}

export const displayOrDash = (v) =>
  v == null || v === '' ? DASH : String(v);

// ─── Logo ────────────────────────────────────────────────────────────────────
/**
 * Draw an image with its aspect ratio preserved, anchored either on a centre line
 * (`centerX`) or a right edge (`rightX`).
 *
 * Pass `draw: false` to measure without drawing — that is how no-letterhead mode
 * reserves exactly the space the logo would have taken.
 *
 * @returns {number|null} bottom y of the image, or null if it could not be measured
 */
export function addLogo(doc, { src, centerX, rightX, top, maxW, maxH, draw = true }) {
  const image = src || NavLogoHlthTek;
  try {
    const props = doc.getImageProperties(image);
    const ratio = props.width / props.height;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    if (draw) {
      const x = centerX != null ? centerX - w / 2 : rightX - w;
      doc.addImage(image, 'PNG', x, top, w, h);
    }
    return top + h;
  } catch (_) {
    return null;
  }
}

// ─── Letterhead ──────────────────────────────────────────────────────────────
/**
 * Centred letterhead: logo, facility name, address, registration line, rule.
 *
 * Lines with no real value are skipped along with their vertical advance, so a
 * facility that has only a name and address still gets a clean, gapless stack.
 *
 * With `letterhead: false` every measurement still runs and the returned y is
 * unchanged — only the ink is withheld. That is what lets a document printed on
 * pre-printed stationery land in exactly the same place as one that is not.
 *
 * @returns {number} y immediately below the rule
 */
export function drawLetterhead(doc, identity, registrationLine, facilityLogo, letterhead = true) {
  const W = doc.internal.pageSize.getWidth();
  const cx = W / 2;

  const logoBottom = addLogo(doc, {
    src: facilityLogo,
    centerX: cx,
    top: 10,
    maxW: 40,
    maxH: 16,
    draw: letterhead,
  });

  let y = (logoBottom == null ? 14 : logoBottom) + 7.5;

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.navy);
  // Long facility names step down a size rather than colliding with the margins.
  doc.setFontSize(17.5);
  if (doc.getTextWidth(identity.name) > 170) doc.setFontSize(14);
  if (letterhead) doc.text(identity.name, cx, y, { align: 'center' });

  if (identity.address) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.muted);
    const lines = doc.splitTextToSize(identity.address, U * 0.8).slice(0, 2);
    lines.forEach((line) => {
      y += 5.2;
      if (letterhead) doc.text(line, cx, y, { align: 'center' });
    });
  }

  if (registrationLine) {
    y += 4.6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.faint);
    if (letterhead) doc.text(registrationLine, cx, y, { align: 'center' });
  }

  y += 4.5;
  if (letterhead) {
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(SIDE_M, y, W - SIDE_M, y);
  }

  return y;
}

/**
 * Document title, outlined status chip and subtitle.
 *
 * The chip is stroked rather than filled — that is the v5 treatment, and it keeps the
 * status legible on a black-and-white printer.
 *
 * @returns {number} y below the subtitle
 */
export function drawTitleRow(doc, y, { title, subtitle, chipLabel, chipColor }) {
  const W = doc.internal.pageSize.getWidth();
  const titleY = y + 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...C.navy);
  doc.text(title, SIDE_M, titleY);

  if (chipLabel) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    const w = Math.max(doc.getTextWidth(chipLabel) + 7, 24);
    const x = W - SIDE_M - w;
    const top = titleY - 5;
    doc.setDrawColor(...chipColor);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, top, w, 6.4, 1.6, 1.6, 'S');
    doc.setTextColor(...chipColor);
    doc.text(chipLabel, x + w / 2, top + 4.3, { align: 'center' });
  }

  let out = titleY;
  if (subtitle) {
    out = titleY + 4.6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(subtitle, SIDE_M, out);
  }
  return out + 6;
}

// ─── Cards ───────────────────────────────────────────────────────────────────
/**
 * Height of a label/value card. Measured with the same font the values are drawn
 * in — splitTextToSize is font-sensitive and the values are bold, which is wider
 * than normal weight.
 *
 * @returns {{counts: number[], height: number}}
 */
export function measureRows(doc, rows, w) {
  const valueMaxW = w - 2 * CARD_PAD_X - LABEL_W;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const counts = rows.map(([, value]) =>
    Math.min(
      MAX_VALUE_LINES,
      doc.splitTextToSize(displayOrDash(value), valueMaxW).length,
    ),
  );
  const body = counts.reduce((acc, n) => acc + ROW_H + (n - 1) * WRAP_H, 0);
  return { counts, height: CARD_HEAD_H + body + CARD_PAD_BOT };
}

/** Height of a free-text card (BUYER / SHIP TO, remarks, terms). */
export function measureLines(doc, text, w, fontSize = 7) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(String(text ?? ''), w - 2 * CARD_PAD_X);
  const pitch = fontSize <= 6.5 ? 3.5 : LINE_H;
  return { lines, height: CARD_HEAD_H + lines.length * pitch + CARD_PAD_BOT, pitch };
}

/** Stroke-only card border. */
export function strokeCard(doc, x, y, w, h) {
  doc.setDrawColor(...C.border);
  doc.setLineWidth(CARD_BORDER);
  doc.roundedRect(x, y, w, h, CARD_R, CARD_R, 'S');
}

/**
 * Card title and the hairline under it.
 * @returns {number} y of the first body row box
 */
export function drawCardTitle(doc, x, y, w, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.navy);
  doc.text(String(title).toUpperCase(), x + CARD_PAD_X, y + 5.4);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.1);
  doc.line(x + CARD_PAD_X, y + 7.4, x + w - CARD_PAD_X, y + 7.4);
  return y + CARD_HEAD_H;
}

/** Border + title in one call. @returns {number} y of the first body row box */
export function drawCardFrame(doc, x, y, w, h, title) {
  strokeCard(doc, x, y, w, h);
  return drawCardTitle(doc, x, y, w, title);
}

/** Label/value rows inside a card, using the counts from measureRows. */
export function drawRows(doc, x, bodyTop, w, rows, counts) {
  const valueMaxW = w - 2 * CARD_PAD_X - LABEL_W;
  let top = bodyTop;

  rows.forEach(([label, value], i) => {
    const baseline = top + 3.1;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(String(label), x + CARD_PAD_X, baseline);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.navy);
    const lines = doc
      .splitTextToSize(displayOrDash(value), valueMaxW)
      .slice(0, counts[i]);
    lines.forEach((line, li) => {
      doc.text(line, x + CARD_PAD_X + LABEL_W, baseline + li * WRAP_H);
    });

    top += ROW_H + (counts[i] - 1) * WRAP_H;
  });
}

/** Free-text body inside a card, using the lines from measureLines. */
export function drawLines(doc, x, bodyTop, lines, pitch = LINE_H, color = C.navy) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(pitch <= 3.5 ? 6.5 : 7);
  doc.setTextColor(...color);
  lines.forEach((line, i) => {
    doc.text(line, x + CARD_PAD_X, bodyTop + 3.1 + i * pitch);
  });
}

// ─── Summary card ────────────────────────────────────────────────────────────
/** Height of a summary card with `rows` breakdown lines plus the total row. */
export function measureSummary(rows) {
  return CARD_HEAD_H + rows.length * ROW_H + 10.5 + CARD_PAD_BOT;
}

/**
 * Right-aligned money breakdown, a rule, then the total.
 *
 * The total is bold navy text above a rule — v5 dropped the filled navy bar the
 * previous design used.
 */
export function drawSummaryCard(doc, x, y, w, h, title, rows, totalLabel, totalValue) {
  const bodyTop = drawCardFrame(doc, x, y, w, h, title);
  const rightX = x + w - CARD_PAD_X;
  let ry = bodyTop;

  rows.forEach(([label, value]) => {
    const baseline = ry + 3.1;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(String(label), x + CARD_PAD_X, baseline);
    doc.setTextColor(...C.navy);
    doc.text(String(value), rightX, baseline, { align: 'right' });
    ry += ROW_H;
  });

  ry += 1.4;
  doc.setDrawColor(...C.navy);
  doc.setLineWidth(0.3);
  doc.line(x + CARD_PAD_X, ry, rightX, ry);

  ry += 4.6;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.navy);
  doc.setFontSize(8.5);
  doc.text(String(totalLabel), x + CARD_PAD_X, ry);
  doc.setFontSize(10);
  doc.text(String(totalValue), rightX, ry, { align: 'right' });
}

// ─── Amount-in-words + remarks card ──────────────────────────────────────────
export function measureWordsCard(doc, w, words, remarks) {
  const inner = w - 2 * CARD_PAD_X;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const wordLines = doc.splitTextToSize(String(words || ''), inner);

  let remarkLines = [];
  const text = String(remarks ?? '').trim();
  if (text) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    remarkLines = doc.splitTextToSize(text, inner);
  }

  const height =
    CARD_HEAD_H +
    wordLines.length * LINE_H +
    (remarkLines.length ? 4.4 + remarkLines.length * 3.5 : 0) +
    CARD_PAD_BOT;

  return { height, wordLines, remarkLines };
}

export function drawWordsCard(doc, x, y, w, h, title, wordLines, remarkLines) {
  const bodyTop = drawCardFrame(doc, x, y, w, h, title);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...C.navy);
  wordLines.forEach((line, i) => {
    doc.text(line, x + CARD_PAD_X, bodyTop + 3.1 + i * LINE_H);
  });

  if (!remarkLines.length) return;

  const ry = bodyTop + 3.1 + wordLines.length * LINE_H + 3.2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(...C.muted);
  doc.text('REMARKS', x + CARD_PAD_X, ry);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.navy);
  remarkLines.forEach((line, i) => {
    doc.text(line, x + CARD_PAD_X, ry + 3.5 + i * 3.5);
  });
}

// ─── Items table ─────────────────────────────────────────────────────────────
/**
 * Shared autoTable style block. Rows carry no borders of their own — separators are
 * drawn as hairlines in didDrawCell (see drawRowRule) so the table reads as v5's
 * horizontal rules rather than a full grid.
 *
 * valign is 'top' so the HSN sub-line lands at a predictable offset.
 */
export function tableStyles() {
  return {
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 7,
      textColor: C.navy,
      valign: 'top',
      lineWidth: 0,
      cellPadding: { top: 1.8, bottom: 3, left: 1.6, right: 1.6 },
      minCellHeight: 9,
    },
    headStyles: {
      fontSize: 6.5,
      fontStyle: 'bold',
      textColor: C.navy,
      fillColor: C.headBg,
      lineWidth: 0,
      cellPadding: { top: 2, bottom: 2, left: 1.6, right: 1.6 },
    },
  };
}

/** Hairline under a table row. Call from didDrawCell on the first column only. */
export function drawRowRule(doc, data, left, right) {
  doc.setDrawColor(...C.border);
  doc.setLineWidth(data.section === 'head' ? 0.2 : 0.1);
  const y = data.cell.y + data.row.height;
  doc.line(left, y, right, y);
}

/** The muted `HSN: … · manufacturer` line under an item description. */
export function drawItemSubline(doc, data, parts) {
  const text = parts.filter(Boolean).join('  ·  ');
  if (!text) return;
  const lineCount = data.cell.text?.length || 1;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...C.faint);
  doc.text(text, data.cell.x + 1.6, data.cell.y + 1.8 + lineCount * 2.85 + 1.9);
}

// ─── Pagination ──────────────────────────────────────────────────────────────
/**
 * Make sure `needed` mm are available, starting a new page if not. Cards are atomic —
 * they are measured, fitted, then drawn, so none is ever split across a page.
 *
 * The Math.min guard means a card taller than a whole page breaks once and then
 * overflows rather than looping forever.
 */
export function ensureSpace(doc, y, needed, onNewPage) {
  const maxUsable = CONTENT_BOTTOM - CONT_TOP;
  if (y + Math.min(needed, maxUsable) <= CONTENT_BOTTOM) return y;
  doc.addPage();
  if (onNewPage) onNewPage(doc);
  return CONT_TOP;
}

/**
 * Compact header for pages 2+.
 *
 * Without the letterhead only the document reference survives — page 2 of a run on
 * pre-printed stationery still needs to say which PO or GRN it belongs to, but the
 * facility identity is already on the paper. Coordinates do not change.
 */
export function drawContinuationHeader(doc, identity, docTitle, docNumber, facilityLogo, letterhead = true) {
  const W = doc.internal.pageSize.getWidth();

  if (letterhead) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.navy);
    doc.text(identity.name, SIDE_M, 12);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.muted);
  doc.text(`${docTitle}  ·  ${displayOrDash(docNumber)}  (continued)`, SIDE_M, 16.5);

  if (letterhead) {
    addLogo(doc, { src: facilityLogo, rightX: W - SIDE_M, top: 8, maxW: 24, maxH: 9 });
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.2);
    doc.line(SIDE_M, 19.5, W - SIDE_M, 19.5);
  }
}

// ─── Signature + footer ──────────────────────────────────────────────────────
export function drawSignatureRow(doc, y, leftLabel, rightLabel) {
  const W = doc.internal.pageSize.getWidth();
  const sigY = y + 10;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(SIDE_M, sigY, SIDE_M + 60, sigY);
  doc.line(W - SIDE_M - 60, sigY, W - SIDE_M, sigY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.muted);
  doc.text(leftLabel, SIDE_M, sigY + 4);
  doc.text(rightLabel, W - SIDE_M, sigY + 4, { align: 'right' });
}

/**
 * Hairline footer — drawn on every page once the page count is known.
 *
 * The page number is not branding, so it prints either way; the facility name, the
 * Hlthtek line, the rule and the product logo are suppressed without the letterhead.
 * The reserved band (FOOTER_RESERVE) is unchanged, so the table breaks identically.
 */
export function drawFooter(doc, identity, pageNum, totalPages, letterhead = true) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  if (letterhead) {
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.2);
    doc.line(SIDE_M, H - 16, W - SIDE_M, H - 16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.navy);
    doc.text(identity.name, SIDE_M, H - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...C.muted);
    doc.text(FOOTER_POWERED, SIDE_M, H - 8.2);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.muted);
  doc.text(`Page ${pageNum} of ${totalPages}`, W - SIDE_M, H - 12, { align: 'right' });

  if (letterhead) {
    addLogo(doc, { rightX: W - SIDE_M, top: H - 9.6, maxW: 22, maxH: 6 });
  }
}

/** Stamp the footer on every page. Call last, once the page count is final. */
export function stampFooters(doc, identity, letterhead = true) {
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    drawFooter(doc, identity, p, total, letterhead);
  }
}
